import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import type { DeliveryFilterValues } from '@/features/deliveries';
import type { DeliveryExportInput, DeliveryFilterInput } from '@/services/db/delivery/utils';

/**
 * URL-backed Outbox filter state. Keys mirror the Ember query params, and the
 * parsed shape matches `DeliveryFilterValues` (so the page forwards it straight
 * to `<DeliveryFilters>`): `actionType` defaults to `''` (all channels); the
 * rest are `null`/absent when unset. The `satisfies` keeps this map exhaustive
 * against `DeliveryFilterValues` — adding a filter field there fails here until a
 * parser is added, so a new URL param can't be silently dropped.
 */
const OUTBOX_FILTER_PARSERS = {
  actionType: parseAsString.withDefault(''),
  campaign: parseAsString,
  action: parseAsString,
  recipient: parseAsString,
  deliveryStatus: parseAsString,
  startTs: parseAsInteger,
  endTs: parseAsInteger,
} satisfies Record<keyof DeliveryFilterValues, unknown>;

export function useOutboxFilters(): {
  values: DeliveryFilterValues;
  onChange: (patch: Partial<DeliveryFilterValues>) => void;
} {
  const [values, setValues] = useQueryStates(OUTBOX_FILTER_PARSERS);
  return { values, onChange: (patch) => void setValues(patch) };
}

/** Whether any filter is active — drives the table's filter-aware empty state. */
export function hasActiveFilters(v: DeliveryFilterValues): boolean {
  return (
    v.actionType !== '' ||
    v.campaign != null ||
    v.action != null ||
    v.recipient != null ||
    v.deliveryStatus != null ||
    v.startTs != null ||
    v.endTs != null
  );
}

/** Filter values → the data layer's list-query input (`null` ⇒ omitted). */
export function toFilterInput(v: DeliveryFilterValues): DeliveryFilterInput {
  return {
    actionType: v.actionType,
    campaign: v.campaign ?? undefined,
    action: v.action ?? undefined,
    recipient: v.recipient ?? undefined,
    deliveryStatus: v.deliveryStatus ?? undefined,
    startTs: v.startTs ?? undefined,
    endTs: v.endTs ?? undefined,
  };
}

/** Format an epoch-second timestamp as `MMM DD, YYYY` in UTC (the Ember date-range copy). */
function formatUtcDate(epochSeconds: number): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(epochSeconds * 1000),
  );
}

/**
 * The filter-aware empty-state sentence (Ember `cio-deliveries` `emptyMessage`):
 * "There are no <noun> [matching your filters] [for the selected time range (…)]".
 * The `noun` is passed in (this module can't call the taxonomy hook) — the
 * deliveries/sent noun, or "drafts" on the drafts tab.
 */
export function buildEmptyMessage(v: DeliveryFilterValues, drafted: boolean, deliveriesNoun: string): string {
  const parts = ['There are no', drafted ? 'drafts' : deliveriesNoun];
  const hasQueryFilters =
    v.actionType !== '' || v.campaign != null || v.action != null || v.recipient != null || v.deliveryStatus != null;
  if (hasQueryFilters) {
    parts.push('matching your filters');
  }
  if (v.startTs != null && v.endTs != null) {
    parts.push(`for the selected time range (${formatUtcDate(v.startTs)} - ${formatUtcDate(v.endTs)})`);
  }
  return parts.join(' ');
}

/** Filter values → the CSV export input (the export modal adds its own date range). */
export function toExportInput(v: DeliveryFilterValues): DeliveryExportInput {
  return {
    actionType: v.actionType,
    deliveryStatus: v.deliveryStatus ?? undefined,
    campaign: v.campaign ?? undefined,
    action: v.action ?? undefined,
    startTs: v.startTs ?? undefined,
    endTs: v.endTs ?? undefined,
  };
}
